import React, { useEffect, useMemo, useRef, useState } from "react";

export default function TeamSection({ lang, founders }) {
  const marqueeRef = useRef(null);
  const trackRef = useRef(null);
  const set1Ref = useRef(null);

  const rafRef = useRef(0);
  const lastRef = useRef(0);
  const xRef = useRef(0);
  const resumeTimerRef = useRef(null);

  const [setWidth, setSetWidth] = useState(0);
  const [repeatCount, setRepeatCount] = useState(6);
  const [paused, setPaused] = useState(false);
  const [step, setStep] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);

  const list = useMemo(() => (Array.isArray(founders) ? founders : []), [founders]);

  const pause = () => setPaused(true);
  const resume = () => setPaused(false);

  const pauseAndAutoResume = (ms = 1400) => {
    setPaused(true);
    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = setTimeout(() => setPaused(false), ms);
  };

  useEffect(() => {
    const measure = () => {
      if (!marqueeRef.current || !trackRef.current || !set1Ref.current) return;

      const marqueeW = marqueeRef.current.clientWidth || 0;
      const w = set1Ref.current.scrollWidth || 0;
      if (!w) return;

      const trackStyles = window.getComputedStyle(trackRef.current);
      const gap = parseFloat(trackStyles.gap || trackStyles.columnGap || "0") || 0;

      const shift = w + gap;
      setSetWidth(shift);

      const firstCard = set1Ref.current.querySelector(".aq-team-card");
      if (firstCard) {
        const cardW = firstCard.getBoundingClientRect().width || 0;
        if (cardW) setStep(cardW + gap);
      }

      const needed = Math.max(6, Math.ceil((marqueeW * 3) / shift));
      setRepeatCount(needed);
    };

    requestAnimationFrame(measure);

    const ro = new ResizeObserver(measure);
    if (set1Ref.current) ro.observe(set1Ref.current);
    if (marqueeRef.current) ro.observe(marqueeRef.current);

    window.addEventListener("resize", measure);

    const imgs = set1Ref.current?.querySelectorAll("img") || [];
    imgs.forEach((img) => {
      if (!img.complete) img.addEventListener("load", measure, { once: true });
    });

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [list]);

  useEffect(() => {
    if (!trackRef.current || !setWidth) return;

    const speed = 0.08;

    const animate = (t) => {
      if (!lastRef.current) lastRef.current = t;
      const dt = t - lastRef.current;
      lastRef.current = t;

      if (!paused) {
        xRef.current += dt * speed;
        if (xRef.current >= setWidth) xRef.current -= setWidth;
        trackRef.current.style.transform = `translate3d(${-xRef.current}px,0,0)`;
      }

      if (step > 0 && list.length > 0) {
        const idx = Math.round((xRef.current / step) % list.length);
        const safeIdx = ((idx % list.length) + list.length) % list.length;
        if (safeIdx !== activeIndex) setActiveIndex(safeIdx);
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
      lastRef.current = 0;
    };
  }, [setWidth, paused, step, list.length, activeIndex]);

  useEffect(() => {
    return () => {
      if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    };
  }, []);

  const copies = useMemo(() => Array.from({ length: repeatCount }), [repeatCount]);

  const applyX = (newX) => {
    if (!trackRef.current || !setWidth) return;
    const normalized = ((newX % setWidth) + setWidth) % setWidth;
    xRef.current = normalized;
    trackRef.current.style.transform = `translate3d(${-xRef.current}px,0,0)`;
  };

  const goToIndex = (idx) => {
    if (!step || !list.length) return;
    const target = idx * step;
    applyX(target);
    setActiveIndex(idx);
    pauseAndAutoResume();
  };

  const next = () => {
    if (!step || !list.length) return;
    const idx = (activeIndex + 1) % list.length;
    goToIndex(idx);
  };

  const prev = () => {
    if (!step || !list.length) return;
    const idx = (activeIndex - 1 + list.length) % list.length;
    goToIndex(idx);
  };

  const Card = ({ f, cardKey }) => (
    <article
      className="aq-team-card aq-team-card--clean aq-team-card--sm"
      key={cardKey}
      onMouseEnter={pause}
      onMouseLeave={resume}
      onTouchStart={pause}
      onTouchEnd={resume}
      onTouchCancel={resume}
    >
      <div className="aq-team-media aq-team-media--sm">
        <img
          className="aq-team-avatar-square"
          src={f.img}
          alt={f.name}
          loading="lazy"
          draggable="false"
        />
      </div>

      <div className="aq-team-body aq-team-body--sm">
        <div className="aq-team-title">
          <div className="aq-team-name">{f.name}</div>
          <div className="aq-team-role">{f.role}</div>
        </div>

        <p className="aq-team-note">{f.note}</p>

        <div className="aq-team-actions">
          <a className="aq-btn aq-btn-solid aq-btn-linkedin" href={f.linkedin} target="_blank" rel="noreferrer">
            {lang === "ar" ? "تواصل" : "contact"}
          </a>
        </div>
      </div>
    </article>
  );

  return (
    <section className="aq-section aq-team" id="founders">
      <div className="aq-container">
        <div className="aq-section-head aq-team-head">
          <h2 className="aq-headline">{lang === "ar" ? "فريق Aman-Qo" : "Aman-Qo Team"}</h2>
          <p className="aq-muted">
            {lang === "ar"
              ? "ناس حقيقيين… شغالين على إن الأمن الرقمي يبقى بسيط ومفهوم."
              : "Real people building simple, practical digital safety."}
          </p>
        </div>

        <div
          className="aq-team-marquee"
          ref={marqueeRef}
          style={{
            "--aq-card-w": "320px",
            "--aq-card-w-md": "300px",
            "--aq-card-w-sm": "86vw",
          }}
        >
          <div className="aq-team-track" ref={trackRef}>
            <div className="aq-team-set" ref={set1Ref}>
              {list.map((f, idx) => (
                <Card f={f} cardKey={`s1-${f?.name || "member"}-${idx}`} key={`s1-${f?.name || "member"}-${idx}`} />
              ))}
            </div>

            {copies.slice(1).map((_, copyIdx) => (
              <div className="aq-team-set" aria-hidden="true" key={`copy-${copyIdx}`}>
                {list.map((f, idx) => (
                  <Card
                    f={f}
                    cardKey={`c${copyIdx}-${f?.name || "member"}-${idx}`}
                    key={`c${copyIdx}-${f?.name || "member"}-${idx}`}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>

        {list.length > 0 && (
          <div className="aq-team-controls" dir="ltr">
            <button type="button" className="aq-team-nav" onClick={prev} aria-label="Previous">
              ‹
            </button>

            <div className="aq-team-dots" role="tablist" aria-label="Team members">
              {list.map((_, i) => (
                <button
                  key={`dot-${i}`}
                  type="button"
                  className={`aq-team-dot ${i === activeIndex ? "is-active" : ""}`}
                  onClick={() => goToIndex(i)}
                  aria-label={`Go to ${i + 1}`}
                  aria-current={i === activeIndex ? "true" : "false"}
                />
              ))}
            </div>

            <button type="button" className="aq-team-nav" onClick={next} aria-label="Next">
              ›
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
